import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Switch from '@mui/joy/Switch';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Grid from '@mui/joy/Grid';
import { productsApi } from '../../api/products.api';
import { categoriesApi } from '../../api/categories.api';
import { brandsApi } from '../../api/brands.api';
import { attributeSetsApi } from '../../api/attribute-sets.api';
import { attributesApi } from '../../api/attributes.api';
import { CreateProductDto, ProductAttributeValue } from '../../types/product';
import { Category } from '../../types/category';
import { Brand } from '../../types/brand';
import { AttributeSet } from '../../types/attribute-set';
import { Attribute } from '../../types/attribute';
import { formatRecordReference } from '../../utils/formatRecordReference';

interface AttributeFieldValue {
  attributeId?: number;
  attributeReference: string;
  attribute: Attribute;
  valueText?: string;
  valueNumber?: number;
  valueBoolean?: boolean;
  valueDate?: string;
  valueOptionId?: number;
  isRequired: boolean;
}

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference?: string }>();
  const productReference = reference as string | undefined;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateProductDto>({
    name: '',
    price: 0,
    quantity: 0,
    sku: '',
    productType: 'simple',
    categoryReference: undefined,
    brandReference: undefined,
    attributeValues: [],
  });
  const [priceInput, setPriceInput] = useState('');
  const [quantityInput, setQuantityInput] = useState('0');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [attributeSet, setAttributeSet] = useState<AttributeSet | null>(null);
  const [attributeFields, setAttributeFields] = useState<AttributeFieldValue[]>([]);
  const [allAttributes, setAllAttributes] = useState<Attribute[]>([]);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        const [cats, brds, attrs] = await Promise.all([
          categoriesApi.getAll(),
          brandsApi.getAll(),
          attributesApi.getAll(),
        ]);
        setCategories(cats);
        setBrands(brds);
        setAllAttributes(attrs);
        if (productReference) {
          await loadProduct(productReference, cats, brds, attrs);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, [productReference]);

  const loadProduct = async (ref: string, cats: Category[], brds: Brand[], attrs: Attribute[]) => {
    try {
      const product = await productsApi.getOne(ref);
      setFormData({
        name: product.name,
        price: product.price,
        quantity: product.quantity,
        sku: product.sku || '',
        productType: product.productType || 'simple',
        categoryReference: product.category?.reference,
        brandReference: product.brand?.reference,
        attributeValues: product.attributeValues || [],
      });
      setPriceInput(product.price.toString());
      setQuantityInput(product.quantity.toString());

      if (product.category?.reference) {
        const cat = cats.find((c) => c.reference === product.category?.reference);
        if (cat) {
          setSelectedCategory(cat);
          if (cat.attributeSetId) {
            await loadAttributeSetForCategory(cat, attrs, product.attributeValues);
          }
        }
      }

      if (product.brand?.reference) {
        const br = brds.find((b) => b.reference === product.brand?.reference);
        if (br) setSelectedBrand(br);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
  };

  const loadAttributeSetForCategory = async (
    cat: Category,
    attrs: Attribute[],
    existingValues?: ProductAttributeValue[],
  ) => {
    const setRef = cat.attributeSet?.reference;
    if (!setRef) {
      setAttributeSet(null);
      setAttributeFields([]);
      return;
    }
    try {
      const detail = await attributeSetsApi.getOne(setRef);
      if (!detail || !detail.items || detail.items.length === 0) {
        setAttributeSet(detail || null);
        setAttributeFields([]);
        return;
      }
      setAttributeSet(detail);
      buildFieldsFromSet(detail, attrs, existingValues);
    } catch (error) {
      console.error('Error loading attribute set:', error);
      setAttributeSet(null);
      setAttributeFields([]);
    }
  };

  const buildFieldsFromSet = (
    set: AttributeSet,
    attrs: Attribute[],
    existingValues?: ProductAttributeValue[],
  ) => {
    if (!set.items || set.items.length === 0) {
      setAttributeFields([]);
      return;
    }

    const fields: AttributeFieldValue[] = set.items
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => {
        const attr = item.attribute
          || attrs.find((a) => a.reference === item.attribute?.reference || a.code === item.attribute?.code);
        if (!attr) return null;

        const existing = existingValues?.find(
          (v) => v.attributeReference === attr.reference,
        );

        return {
          attributeId: item.attributeId,
          attributeReference: attr.reference,
          attribute: attr,
          valueText: existing?.valueText || '',
          valueNumber: existing?.valueNumber || undefined,
          valueBoolean: existing?.valueBoolean || false,
          valueDate: existing?.valueDate || '',
          valueOptionId: existing?.valueOptionId || undefined,
          isRequired: item.isRequired,
        };
      })
      .filter(Boolean) as AttributeFieldValue[];

    setAttributeFields(fields);
  };

  const handleCategoryChange = async (catRef: string | null) => {
    if (!catRef) {
      setSelectedCategory(null);
      setAttributeSet(null);
      setAttributeFields([]);
      setFormData((prev) => ({ ...prev, categoryReference: undefined, attributeValues: [] }));
      return;
    }

    const cat = categories.find((c) => c.reference === catRef);
    if (cat) {
      setSelectedCategory(cat);
      setFormData((prev) => ({ ...prev, categoryReference: cat.reference }));
      await loadAttributeSetForCategory(cat, allAttributes);
    }
  };

  const handleBrandChange = (brandRef: string | null) => {
    if (!brandRef) {
      setSelectedBrand(null);
      setFormData((prev) => ({ ...prev, brandReference: undefined }));
      return;
    }
    const br = brands.find((b) => b.reference === brandRef);
    if (br) {
      setSelectedBrand(br);
      setFormData((prev) => ({ ...prev, brandReference: br.reference }));
    }
  };

  const updateAttributeField = (index: number, update: Partial<AttributeFieldValue>) => {
    setAttributeFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...update };
      return next;
    });
  };

  const buildAttributeValuesFromFields = (): ProductAttributeValue[] => {
    return attributeFields
      .filter((f) => {
        if (f.attribute.dataType === 'boolean') return true;
        if (f.valueText || f.valueNumber !== undefined || f.valueOptionId || f.valueDate) return true;
        return false;
      })
      .map((f) => ({
        attributeId: f.attributeId,
        attributeReference: f.attributeReference,
        valueText: f.valueText || undefined,
        valueNumber: f.valueNumber,
        valueBoolean: f.valueBoolean,
        valueDate: f.valueDate || undefined,
        valueOptionId: f.valueOptionId,
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const attributeValues = buildAttributeValuesFromFields();
      const finalData: CreateProductDto = {
        ...formData,
        price: parseFloat(priceInput) || 0,
        quantity: parseInt(quantityInput) || 0,
        productType: 'simple',
        attributeValues,
      };

      if (productReference) {
        await productsApi.update(productReference, finalData);
      } else {
        await productsApi.create(finalData);
      }
      navigate('/products');
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setSaving(false);
    }
  };

  const renderAttributeField = (field: AttributeFieldValue, index: number) => {
    const { attribute } = field;

    switch (attribute.dataType) {
      case 'text':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Input
              value={field.valueText || ''}
              onChange={(e) => updateAttributeField(index, { valueText: e.target.value })}
              placeholder={attribute.name}
            />
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Textarea
              minRows={2}
              value={field.valueText || ''}
              onChange={(e) => updateAttributeField(index, { valueText: e.target.value })}
              placeholder={attribute.name}
            />
          </FormControl>
        );

      case 'number':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Input
              type="number"
              value={field.valueNumber?.toString() || ''}
              onChange={(e) => updateAttributeField(index, { valueNumber: parseFloat(e.target.value) || undefined })}
              placeholder={attribute.name}
            />
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControl key={field.attributeReference} orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <FormLabel>{attribute.name}</FormLabel>
            <Switch
              checked={field.valueBoolean || false}
              onChange={(e) => updateAttributeField(index, { valueBoolean: e.target.checked })}
            />
          </FormControl>
        );

      case 'date':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Input
              type="date"
              value={field.valueDate || ''}
              onChange={(e) => updateAttributeField(index, { valueDate: e.target.value })}
            />
          </FormControl>
        );

      case 'select':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Select
              value={field.valueOptionId || null}
              onChange={(_, value) => updateAttributeField(index, { valueOptionId: value as number })}
              placeholder={`Seleccionar ${attribute.name}`}
            >
              {(attribute.options || []).map((opt) => (
                <Option key={opt.id} value={opt.id}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl key={field.attributeReference} required={field.isRequired}>
            <FormLabel>{attribute.name}</FormLabel>
            <Input
              value={field.valueText || ''}
              onChange={(e) => updateAttributeField(index, { valueText: e.target.value })}
              placeholder={`Valores separados por coma`}
            />
          </FormControl>
        );

      case 'gallery':
        return (
          <FormControl key={field.attributeReference}>
            <FormLabel>{attribute.name}</FormLabel>
            <Input
              value={field.valueText || ''}
              onChange={(e) => updateAttributeField(index, { valueText: e.target.value })}
              placeholder="URLs separadas por coma"
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 3 }}>
        {productReference ? `Editar producto #${formatRecordReference(productReference)}` : 'Nuevo producto'}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* COLUMNA PRINCIPAL (8/12) */}
          <Grid xs={12} md={8}>
            <Stack spacing={3}>
              {/* Información básica */}
              <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'md' }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Información básica
                </Typography>
                <Stack spacing={2}>
                  <FormControl required>
                    <FormLabel>Nombre</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre del producto"
                    />
                  </FormControl>

                  <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                      <FormControl required>
                        <FormLabel>Precio</FormLabel>
                        <Input
                          startDecorator="$"
                          value={priceInput}
                          onChange={(e) => setPriceInput(e.target.value.replace(/[^\d.]/g, ''))}
                          placeholder="0.00"
                          type="number"
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl required>
                        <FormLabel>Cantidad</FormLabel>
                        <Input
                          value={quantityInput}
                          onChange={(e) => setQuantityInput(e.target.value.replace(/[^\d]/g, ''))}
                          placeholder="0"
                          type="number"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Stack>
              </Sheet>

              {/* Atributos dinámicos (si existen) */}
              {attributeFields.length > 0 && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'md' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Typography level="title-md">Características</Typography>
                    {attributeSet && (
                      <Chip size="sm" variant="soft" color="primary">
                        {attributeSet.name}
                      </Chip>
                    )}
                  </Stack>
                  <Stack spacing={2}>
                    {attributeFields.map((field, index) => renderAttributeField(field, index))}
                  </Stack>
                </Sheet>
              )}

              {selectedCategory && !attributeSet && !selectedCategory.attributeSetId && (
                <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    Esta categoría no tiene un Attribute Set asignado. Podés asignar uno desde la gestión de
                    categorías para habilitar campos dinámicos.
                  </Typography>
                </Sheet>
              )}
            </Stack>
          </Grid>

          {/* SIDEBAR (4/12) */}
          <Grid xs={12} md={4}>
            <Stack spacing={2}>
              {/* Organización */}
              <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 2, fontWeight: 600 }}>
                  Organización
                </Typography>
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Categoría</FormLabel>
                    <Select
                      value={selectedCategory?.reference || null}
                      onChange={(_, value) => handleCategoryChange(value as string | null)}
                      placeholder="Selecciona una categoría"
                    >
                      {categories.map((cat) => (
                        <Option key={cat.reference} value={cat.reference}>
                          {cat.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Marca</FormLabel>
                    <Select
                      value={selectedBrand?.reference || null}
                      onChange={(_, value) => handleBrandChange(value as string | null)}
                      placeholder="Selecciona una marca"
                    >
                      {brands.map((br) => (
                        <Option key={br.reference} value={br.reference}>
                          {br.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Sheet>

              {/* Identificación */}
              <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 2, fontWeight: 600 }}>
                  Identificación
                </Typography>
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>SKU</FormLabel>
                    <Input
                      value={formData.sku || ''}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      placeholder="Código SKU (opcional)"
                    />
                  </FormControl>
                </Stack>
              </Sheet>
            </Stack>
          </Grid>
        </Grid>

        {/* Barra de acciones */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            mt: 4,
          }}
        >
          <Button variant="outlined" color="neutral" onClick={() => navigate('/products')}>
            Cancelar
          </Button>
          <Button type="submit" color="primary" loading={saving}>
            {productReference ? 'Actualizar' : 'Crear'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductForm;
